import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import ExcelJS from 'exceljs';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, LineChart, Line,
} from 'recharts';

const CHART_COLORS = [
  '#0F766E', '#3B82F6', '#8B5CF6', '#F59E0B', '#EF4444',
  '#06B6D4', '#EC4899', '#14B8A6', '#6366F1', '#84CC16',
];

const formatCurrency = (n) => {
  if (n == null || n === '') return '—';
  return new Intl.NumberFormat('es-PE', {
    style: 'currency', currency: 'PEN', minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(n);
};

const formatNumber = (n, decimals = 2) => {
  if (n == null || n === '') return '—';
  return new Intl.NumberFormat('es-PE', {
    minimumFractionDigits: decimals, maximumFractionDigits: decimals,
  }).format(n);
};

const currencyTooltip = (value) => formatCurrency(value);
const numberTooltip = (value, unit = '') => `${formatNumber(value)}${unit ? ` ${unit}` : ''}`;

function SectionTitle({ title, subtitle }) {
  return (
    <div className="pt-2">
      <h2 className="text-lg font-semibold text-gray-800">{title}</h2>
      {subtitle ? <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p> : null}
    </div>
  );
}

function KpiCard({ label, value, sub, borderColor, icon }) {
  return (
    <div className={`bg-white rounded-xl shadow-sm p-5 border-l-4 ${borderColor}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm text-gray-500 mb-1">{label}</p>
          <p className="text-2xl font-bold text-gray-900 truncate">{value}</p>
          {sub ? <p className="text-xs text-gray-500 mt-1">{sub}</p> : null}
        </div>
        {icon ? <span className="text-2xl shrink-0">{icon}</span> : null}
      </div>
    </div>
  );
}

function UsageBarChart({ title, data, dataKey, unit, color = '#0F766E' }) {
  if (!data?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
      <h3 className="text-base font-semibold text-gray-800 mb-4">{title}</h3>
      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={data} layout="vertical" margin={{ left: 8, right: 16 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
          <XAxis type="number" tickFormatter={(v) => formatNumber(v, 0)} />
          <YAxis type="category" dataKey="label" width={110} tick={{ fontSize: 11 }} />
          <Tooltip formatter={(v) => numberTooltip(v, unit)} />
          <Bar dataKey={dataKey} fill={color} radius={[0, 4, 4, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function truncateLabel(label, max = 42) {
  const text = String(label || '');
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1)}…`;
}

function styleExcelHeaderRow(row) {
  const headerFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0F766E' } };
  const headerFont = { bold: true, color: { argb: 'FFFFFFFF' }, size: 10 };
  const thin = { style: 'thin', color: { argb: 'FFD1D5DB' } };
  row.eachCell((cell) => {
    cell.fill = headerFill;
    cell.font = headerFont;
    cell.alignment = { vertical: 'middle', wrapText: true };
    cell.border = { top: thin, left: thin, bottom: thin, right: thin };
  });
}

async function exportChartSustentoExcel({
  title,
  units = [],
  months = [],
  metricKey,
  metricLabel,
  kind,
  details = [],
  efficiencyMode = null,
  fuelDetails = [],
  travelDetails = [],
  meta = {},
}) {
  const wb = new ExcelJS.Workbook();
  wb.creator = 'Contifine Logistic';
  wb.created = new Date();

  const dateFrom = meta?.date_from || '';
  const dateTo = meta?.date_to || '';
  const monthList = months || [];
  const monthLabel = Object.fromEntries(monthList.map((m) => [m.month, m.label]));

  const wsCrit = wb.addWorksheet('Criterio', { views: [{ state: 'frozen', ySplit: 1 }] });
  wsCrit.columns = [{ width: 28 }, { width: 95 }];
  styleExcelHeaderRow(wsCrit.addRow(['Campo', 'Descripción']));
  const criteria = [
    ['Gráfico', title],
    ['Periodo', `${dateFrom || '—'} a ${dateTo || '—'}`],
    ['Métrica del gráfico', metricLabel || metricKey],
  ];
  if (kind === 'fuel') {
    criteria.push(
      ['Fuente', 'Eventos monetarios (machinery_events) con producto de combustible'],
      ['Criterio producto', 'Nombre, SKU o familia del producto sugiere combustible (diesel, gasolina, GLP, etc.)'],
      ['Galones', 'Suma de quantity del ítem del evento, por unidad y mes (fecha del evento)'],
      ['Soles', 'Suma de total_amount del ítem del evento, mismos filtros'],
      ['Etiqueta unidad', 'Código - Nombre - Placa (si aplica)'],
    );
  } else if (kind === 'travel') {
    criteria.push(
      ['Fuente', 'Pares válidos Inicio/Fin de jornada (machinery_shift_journals / eventos de jornada)'],
      ['Km (vehículos)', 'Suma de (odómetro fin − odómetro inicio) del par, mes = fecha fin'],
      ['HM (maquinaria)', 'Suma de (horómetro fin − horómetro inicio) del par, mes = fecha fin'],
      ['Etiqueta unidad', 'Código - Nombre - Placa (si aplica)'],
    );
  } else if (kind === 'efficiency') {
    if (efficiencyMode === 'km_per_gallon') {
      criteria.push(
        ['Fórmula', 'km / galones (por unidad y mes)'],
        ['Numerador', 'Km del mes (misma lógica del gráfico de recorrido)'],
        ['Denominador', 'Galones del mes (misma lógica del gráfico de combustible)'],
        ['Sin dato', 'Si falta km o galones > 0 en el mes, no se muestra eficiencia'],
      );
    } else {
      criteria.push(
        ['Fórmula', 'galones / HM (por unidad y mes)'],
        ['Numerador', 'Galones del mes (misma lógica del gráfico de combustible)'],
        ['Denominador', 'Δ horómetro del mes (misma lógica del gráfico de recorrido)'],
        ['Sin dato', 'Si falta galones o HM > 0 en el mes, no se muestra eficiencia'],
      );
    }
  }
  criteria.forEach((row) => wsCrit.addRow(row));

  const wsSum = wb.addWorksheet('Resumen_grafico', { views: [{ state: 'frozen', ySplit: 1 }] });
  const sumHeaders = ['Unidad', 'Código', ...monthList.map((m) => m.label || m.month), 'Total'];
  styleExcelHeaderRow(wsSum.addRow(sumHeaders));
  wsSum.columns = sumHeaders.map((_, i) => ({ width: i === 0 ? 42 : 14 }));

  (units || []).forEach((u) => {
    let total = 0;
    const vals = monthList.map((m) => {
      const raw = u.by_month?.[m.month]?.[metricKey];
      const v = raw == null || raw === '' ? 0 : Number(raw);
      const n = Number.isFinite(v) ? v : 0;
      total += n;
      return n;
    });
    if (total <= 0 && !vals.some((v) => v > 0)) return;
    wsSum.addRow([u.label || '', u.code || '', ...vals, total]);
  });

  if ((units || []).length && monthList.length) {
    const tot = ['TOTAL', ''];
    let grand = 0;
    monthList.forEach((m) => {
      let s = 0;
      (units || []).forEach((u) => {
        const raw = u.by_month?.[m.month]?.[metricKey];
        const v = raw == null || raw === '' ? 0 : Number(raw);
        if (Number.isFinite(v)) s += v;
      });
      tot.push(s);
      grand += s;
    });
    tot.push(grand);
    const r = wsSum.addRow(tot);
    r.font = { bold: true };
  }

  if (kind === 'fuel') {
    const wsDet = wb.addWorksheet('Detalle_eventos', { views: [{ state: 'frozen', ySplit: 1 }] });
    const headers = [
      'Mes', 'Unidad', 'Código', 'Evento ID', 'Fecha', 'Hora', 'Tipo evento',
      'SKU producto', 'Producto', 'Galones', 'Soles', 'Criterio',
    ];
    styleExcelHeaderRow(wsDet.addRow(headers));
    wsDet.columns = headers.map(() => ({ width: 16 }));
    (details || []).forEach((d) => {
      wsDet.addRow([
        monthLabel[d.month] || d.month,
        d.unit_label,
        d.unit_code,
        d.event_id,
        d.event_date,
        d.event_time,
        d.event_type,
        d.product_sku,
        d.product_name,
        Number(d.gallons || 0),
        Number(d.soles || 0),
        d.criterio || '',
      ]);
    });
  } else if (kind === 'travel') {
    const wsDet = wb.addWorksheet('Detalle_jornadas', { views: [{ state: 'frozen', ySplit: 1 }] });
    const headers = [
      'Mes', 'Unidad', 'Código',
      'Fecha inicio', 'Hora inicio', 'Fecha fin', 'Hora fin',
      'Odómetro inicio', 'Odómetro fin', 'Km',
      'Horómetro inicio', 'Horómetro fin', 'Δ HM', 'Horas', 'Criterio',
    ];
    styleExcelHeaderRow(wsDet.addRow(headers));
    wsDet.columns = headers.map(() => ({ width: 14 }));
    (details || []).forEach((d) => {
      wsDet.addRow([
        monthLabel[d.month] || d.month,
        d.unit_label,
        d.unit_code,
        d.start_date,
        d.start_time,
        d.end_date,
        d.end_time,
        d.start_kilometers,
        d.end_kilometers,
        d.kilometers == null ? null : Number(d.kilometers),
        d.start_horometro,
        d.end_horometro,
        d.horometro == null ? null : Number(d.horometro),
        d.hours == null ? null : Number(d.hours),
        d.criterio || '',
      ]);
    });
  } else if (kind === 'efficiency') {
    const wsCalc = wb.addWorksheet('Calculo_eficiencia', { views: [{ state: 'frozen', ySplit: 1 }] });
    const headers = efficiencyMode === 'km_per_gallon'
      ? ['Mes', 'Unidad', 'Código', 'Km', 'Galones', 'Fórmula', 'Resultado (km/gal)']
      : ['Mes', 'Unidad', 'Código', 'Galones', 'Δ HM', 'Fórmula', 'Resultado (gal/HM)'];
    styleExcelHeaderRow(wsCalc.addRow(headers));
    wsCalc.columns = headers.map(() => ({ width: 16 }));
    (units || []).forEach((u) => {
      Object.entries(u.by_month || {}).forEach(([month, cell]) => {
        const gallons = Number(cell.gallons || 0);
        const km = Number(cell.kilometers || 0);
        const hm = Number(cell.horometro || 0);
        const result = Number(cell[metricKey] || 0);
        if (!(result > 0)) return;
        if (efficiencyMode === 'km_per_gallon') {
          wsCalc.addRow([
            monthLabel[month] || month,
            u.label,
            u.code,
            km,
            gallons,
            cell.formula || `${km} ÷ ${gallons}`,
            result,
          ]);
        } else {
          wsCalc.addRow([
            monthLabel[month] || month,
            u.label,
            u.code,
            gallons,
            hm,
            cell.formula || `${gallons} ÷ ${hm}`,
            result,
          ]);
        }
      });
    });

    if ((fuelDetails || []).length) {
      const wsFuel = wb.addWorksheet('Detalle_combustible', { views: [{ state: 'frozen', ySplit: 1 }] });
      styleExcelHeaderRow(wsFuel.addRow(['Mes', 'Unidad', 'Evento ID', 'Fecha', 'Producto', 'Galones', 'Soles']));
      fuelDetails.forEach((d) => {
        wsFuel.addRow([
          monthLabel[d.month] || d.month,
          d.unit_label,
          d.event_id,
          d.event_date,
          d.product_name,
          Number(d.gallons || 0),
          Number(d.soles || 0),
        ]);
      });
    }
    if ((travelDetails || []).length) {
      const wsTr = wb.addWorksheet('Detalle_jornadas', { views: [{ state: 'frozen', ySplit: 1 }] });
      styleExcelHeaderRow(wsTr.addRow(['Mes', 'Unidad', 'Fecha fin', 'Km', 'Δ HM', 'Criterio']));
      travelDetails.forEach((d) => {
        wsTr.addRow([
          monthLabel[d.month] || d.month,
          d.unit_label,
          d.end_date,
          d.kilometers == null ? null : Number(d.kilometers),
          d.horometro == null ? null : Number(d.horometro),
          d.criterio || '',
        ]);
      });
    }
  }

  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const fileSafe = String(title || 'sustento')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/^_|_$/g, '')
    .slice(0, 48);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `Sustento_${fileSafe}_${dateFrom || 'inicio'}_${dateTo || 'fin'}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Barras agrupadas por unidad × mes (combustible / recorrido / rendimiento). */
function GroupedUnitMonthBarChart({
  title,
  units,
  months,
  metricKey,
  unit = '',
  currency = false,
  layout = 'horizontal',
  exportConfig = null,
  meta = {},
}) {
  const [exporting, setExporting] = useState(false);
  const monthList = months || [];
  const chartData = (units || [])
    .map((row) => {
      const item = {
        label: truncateLabel(row.label),
        fullLabel: row.label || row.code || '',
      };
      let hasValue = false;
      monthList.forEach((m) => {
        const raw = row.by_month?.[m.month]?.[metricKey];
        const value = raw == null || raw === '' ? null : Number(raw);
        item[m.month] = value != null && !Number.isNaN(value) ? value : null;
        if (item[m.month] != null && item[m.month] > 0) hasValue = true;
      });
      return hasValue ? item : null;
    })
    .filter(Boolean);

  const hasData = chartData.length > 0 && monthList.length > 0;

  const handleExport = async () => {
    if (!exportConfig || exporting) return;
    setExporting(true);
    try {
      await exportChartSustentoExcel({
        ...exportConfig,
        title,
        units: units || [],
        months: monthList,
        metricKey,
        meta,
      });
    } catch (err) {
      console.error(err);
      window.alert(err?.message || 'No se pudo generar el Excel de sustento.');
    } finally {
      setExporting(false);
    }
  };

  if (!hasData && !exportConfig) return null;

  const formatter = (value) => (
    currency ? formatCurrency(value) : numberTooltip(value, unit)
  );

  const height = Math.max(280, chartData.length * (layout === 'horizontal' ? 36 : 48) + 80);

  const header = (
    <div className="flex items-start justify-between gap-3 mb-4">
      <h3 className="text-base font-semibold text-gray-800">{title}</h3>
      {exportConfig ? (
        <button
          type="button"
          onClick={handleExport}
          disabled={exporting || !hasData}
          className="shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg border border-teal-200 bg-teal-50 text-teal-800 hover:bg-teal-100 disabled:opacity-50 disabled:cursor-not-allowed"
          title="Descargar cuadro Excel con el detalle de cómo se calculan estos números"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          {exporting ? 'Generando…' : 'Excel sustento'}
        </button>
      ) : null}
    </div>
  );

  if (!hasData) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
        {header}
        <p className="text-sm text-gray-500 py-8 text-center">Sin datos en el periodo.</p>
      </div>
    );
  }

  if (layout === 'horizontal') {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
        {header}
        <ResponsiveContainer width="100%" height={height}>
          <BarChart data={chartData} layout="vertical" margin={{ left: 8, right: 24, top: 8, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis type="number" tickFormatter={(v) => (currency ? formatNumber(v, 0) : formatNumber(v, 1))} />
            <YAxis type="category" dataKey="label" width={180} tick={{ fontSize: 10 }} />
            <Tooltip
              formatter={formatter}
              labelFormatter={(_, payload) => payload?.[0]?.payload?.fullLabel || ''}
            />
            <Legend />
            {monthList.map((m, idx) => (
              <Bar
                key={m.month}
                dataKey={m.month}
                name={m.label}
                fill={CHART_COLORS[idx % CHART_COLORS.length]}
                radius={[0, 4, 4, 0]}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
      {header}
      <ResponsiveContainer width="100%" height={Math.max(320, height)}>
        <BarChart data={chartData} margin={{ left: 8, right: 16, top: 8, bottom: 64 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
          <XAxis dataKey="label" interval={0} angle={-25} textAnchor="end" height={70} tick={{ fontSize: 10 }} />
          <YAxis tickFormatter={(v) => formatNumber(v, currency ? 0 : 1)} />
          <Tooltip
            formatter={formatter}
            labelFormatter={(_, payload) => payload?.[0]?.payload?.fullLabel || ''}
          />
          <Legend />
          {monthList.map((m, idx) => (
            <Bar
              key={m.month}
              dataKey={m.month}
              name={m.label}
              fill={CHART_COLORS[idx % CHART_COLORS.length]}
              radius={[4, 4, 0, 0]}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function AssetTypePie({ title, data, unit }) {
  if (!data?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
      <h3 className="text-base font-semibold text-gray-800 mb-4">{title}</h3>
      <ResponsiveContainer width="100%" height={240}>
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            innerRadius={45}
            outerRadius={80}
            paddingAngle={2}
            dataKey="value"
            nameKey="name"
            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
          >
            {data.map((_, i) => (
              <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
            ))}
          </Pie>
          <Tooltip formatter={(v) => numberTooltip(v, unit)} />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}

function OperationalTable({ title, rows, highlightKey, highlightLabel, highlightUnit }) {
  if (!rows?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      <h3 className="text-base font-semibold text-gray-800 px-5 py-4 border-b border-gray-100">{title}</h3>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Código</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-600 uppercase">{highlightLabel}</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-600 uppercase">Horas</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-600 uppercase">Km</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-600 uppercase">Δ Horóm.</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-600 uppercase">Jornadas</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((r, i) => (
              <tr key={i} className="hover:bg-gray-50/50">
                <td className="px-4 py-2 text-gray-900">{r.code}</td>
                <td className="px-4 py-2 text-right font-medium text-teal-700">
                  {formatNumber(r[highlightKey], highlightUnit === 'km' ? 0 : 2)}{highlightUnit ? ` ${highlightUnit}` : ''}
                </td>
                <td className="px-4 py-2 text-right">{formatNumber(r.total_hours)}</td>
                <td className="px-4 py-2 text-right">{formatNumber(r.total_kilometers, 0)}</td>
                <td className="px-4 py-2 text-right">{formatNumber(r.total_horometro)}</td>
                <td className="px-4 py-2 text-right">{formatNumber(r.shift_pairs_count, 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function MachineryReportsDashboard({ data, maintenanceAlerts, meta }) {
  if (!data) return null;

  const { kpis, usage_kpis: usageKpis = {} } = data;
  const alertsUrgent = (maintenanceAlerts || []).filter((a) => a.status === 'alerta' || a.status === 'pronto');

  const hoursByType = data.hours_by_asset_type || [];
  const kmByType = data.kilometers_by_asset_type || [];
  const horoByType = data.horometro_by_asset_type || [];
  const usageByLine = (data.usage_by_line || []).filter(
    (r) => (r.total_hours ?? 0) > 0 || (r.total_kilometers ?? 0) > 0 || (r.total_horometro ?? 0) > 0
  );
  const usageByMonth = data.usage_by_month || [];

  const topByHours = (data.top_by_hours || []).map((r) => ({ ...r, label: r.code || r.label }));
  const topByKm = (data.top_by_kilometers || []).map((r) => ({ ...r, label: r.code || r.label }));
  const topByHoro = (data.top_by_horometro || []).map((r) => ({ ...r, label: r.code || r.label }));

  const lineHoursData = usageByLine.filter((r) => r.total_hours > 0).slice(0, 10);
  const lineKmData = usageByLine.filter((r) => r.total_kilometers > 0).slice(0, 10);

  const assetTypeData = (data.spend_by_asset_type || []).filter((d) => d.value > 0);
  const lineData = (data.spend_by_line || []).slice(0, 10);
  const eventTypeData = (data.spend_by_event_type || []).slice(0, 8);
  const monthData = data.spend_by_month || [];
  const topVehicles = data.top_vehicles || [];
  const topMachines = data.top_machines || [];

  const topAssetsData = [
    ...topVehicles.map((v) => ({ name: v.code || v.label, value: v.total_spend, type: 'Vehículo' })),
    ...topMachines.map((m) => ({ name: m.code || m.label, value: m.total_spend, type: 'Máquina' })),
  ]
    .sort((a, b) => b.value - a.value)
    .slice(0, 10);

  const hasUsage = hoursByType.length > 0 || kmByType.length > 0 || horoByType.length > 0
    || usageByMonth.length > 0 || topByHours.length > 0;
  const hasFinance = assetTypeData.length > 0 || lineData.length > 0 || monthData.length > 0;

  return (
    <div className="p-4 md:p-6 space-y-8">
      {meta && (
        <p className="text-sm text-gray-500">
          Período {meta.date_from} a {meta.date_to}. Horas, km y horómetro calculados por pares Inicio/Fin de Jornada.
        </p>
      )}

      {/* ─── USO OPERATIVO ─── */}
      <section className="space-y-6">
        <SectionTitle
          title="Uso operativo"
          subtitle="Horas laboradas, kilómetros recorridos y horómetro utilizado en jornadas"
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard
            label="Horas laboradas"
            value={formatNumber(usageKpis.total_hours)}
            sub={[
              usageKpis.vehicle_hours != null ? `Veh. ${formatNumber(usageKpis.vehicle_hours)} h` : null,
              usageKpis.machine_hours != null ? `Máq. ${formatNumber(usageKpis.machine_hours)} h` : null,
            ].filter(Boolean).join(' · ') || 'Sin jornadas registradas'}
            borderColor="border-blue-500"
            icon="⏱️"
          />
          <KpiCard
            label="Km recorridos"
            value={formatNumber(usageKpis.total_kilometers, 0)}
            sub={[
              usageKpis.vehicle_kilometers != null ? `Veh. ${formatNumber(usageKpis.vehicle_kilometers, 0)} km` : null,
              usageKpis.machine_kilometers != null ? `Máq. ${formatNumber(usageKpis.machine_kilometers, 0)} km` : null,
            ].filter(Boolean).join(' · ') || 'Sin km en jornadas'}
            borderColor="border-indigo-500"
            icon="🛣️"
          />
          <KpiCard
            label="Horómetro usado (Δ)"
            value={formatNumber(usageKpis.total_horometro)}
            sub={[
              usageKpis.vehicle_horometro != null ? `Veh. ${formatNumber(usageKpis.vehicle_horometro)} h` : null,
              usageKpis.machine_horometro != null ? `Máq. ${formatNumber(usageKpis.machine_horometro)} h` : null,
            ].filter(Boolean).join(' · ') || 'Sin lecturas de horómetro'}
            borderColor="border-violet-500"
            icon="🔧"
          />
          <KpiCard
            label="Jornadas completadas"
            value={formatNumber(usageKpis.shift_pairs_total, 0)}
            sub={`${formatNumber(usageKpis.units_with_usage, 0)} unidades con uso en el período`}
            borderColor="border-sky-500"
            icon="📅"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <AssetTypePie title="Horas: vehículos vs maquinarias" data={hoursByType} unit="h" />
          <AssetTypePie title="Km: vehículos vs maquinarias" data={kmByType} unit="km" />
          <AssetTypePie title="Horómetro: vehículos vs maquinarias" data={horoByType} unit="h" />
        </div>

        {usageByMonth.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
            <h3 className="text-base font-semibold text-gray-800 mb-4">Evolución mensual de uso</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={usageByMonth} margin={{ top: 8, right: 16, left: 8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="hours" tickFormatter={(v) => `${v}h`} />
                <YAxis yAxisId="km" orientation="right" tickFormatter={(v) => `${v} km`} />
                <Tooltip
                  formatter={(value, name) => {
                    if (name === 'Horas') return numberTooltip(value, 'h');
                    if (name === 'Km') return numberTooltip(value, 'km');
                    return numberTooltip(value, 'h');
                  }}
                />
                <Legend />
                <Line yAxisId="hours" type="monotone" dataKey="total_hours" stroke="#3B82F6" strokeWidth={2} dot={{ r: 3 }} name="Horas" connectNulls />
                <Line yAxisId="km" type="monotone" dataKey="total_kilometers" stroke="#0F766E" strokeWidth={2} dot={{ r: 3 }} name="Km" connectNulls />
                <Line yAxisId="hours" type="monotone" dataKey="total_horometro" stroke="#8B5CF6" strokeWidth={2} dot={{ r: 3 }} name="Δ Horóm." connectNulls />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <UsageBarChart title="Horas laboradas por línea" data={lineHoursData} dataKey="total_hours" unit="h" color="#3B82F6" />
          <UsageBarChart title="Km recorridos por línea" data={lineKmData} dataKey="total_kilometers" unit="km" color="#0F766E" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <UsageBarChart title="Top unidades por horas" data={topByHours} dataKey="value" unit="h" color="#3B82F6" />
          <UsageBarChart title="Top unidades por km" data={topByKm} dataKey="value" unit="km" color="#0F766E" />
          <UsageBarChart title="Top unidades por horómetro" data={topByHoro} dataKey="value" unit="h" color="#8B5CF6" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <OperationalTable
            title="Vehículos — ranking por horas"
            rows={data.top_vehicles_by_hours || []}
            highlightKey="value"
            highlightLabel="Horas"
            highlightUnit="h"
          />
          <OperationalTable
            title="Vehículos — ranking por km"
            rows={data.top_vehicles_by_kilometers || []}
            highlightKey="value"
            highlightLabel="Km"
            highlightUnit="km"
          />
          <OperationalTable
            title="Maquinarias — ranking por horas"
            rows={data.top_machines_by_hours || []}
            highlightKey="value"
            highlightLabel="Horas"
            highlightUnit="h"
          />
          <OperationalTable
            title="Maquinarias — ranking por horómetro"
            rows={data.top_machines_by_horometro || []}
            highlightKey="value"
            highlightLabel="Δ Horóm."
            highlightUnit="h"
          />
        </div>
      </section>

      {/* ─── COMBUSTIBLE Y RECORRIDO ─── */}
      {((data.fuel_by_vehicle_month || []).length > 0
        || (data.fuel_by_machine_month || []).length > 0
        || (data.travel_by_vehicle_month || []).length > 0
        || (data.travel_by_machine_month || []).length > 0
        || (data.efficiency_vehicle_month || []).length > 0
        || (data.efficiency_machine_month || []).length > 0) && (
        <section className="space-y-6">
          <SectionTitle
            title="Combustible y recorrido"
            subtitle="Galones y soles de productos combustible (eventos monetarios); km/HM por jornadas; rendimientos por mes. Vehículos con código y placa."
          />

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <GroupedUnitMonthBarChart
              title="Combustible por vehículos — Galones"
              units={data.fuel_by_vehicle_month}
              months={data.months}
              metricKey="gallons"
              unit="gal"
              layout="horizontal"
              meta={meta}
              exportConfig={{
                kind: 'fuel',
                metricLabel: 'Galones',
                details: data.fuel_detail_vehicle || [],
              }}
            />
            <GroupedUnitMonthBarChart
              title="Combustible por vehículos — Soles"
              units={data.fuel_by_vehicle_month}
              months={data.months}
              metricKey="soles"
              currency
              layout="horizontal"
              meta={meta}
              exportConfig={{
                kind: 'fuel',
                metricLabel: 'Soles (S/)',
                details: data.fuel_detail_vehicle || [],
              }}
            />
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <GroupedUnitMonthBarChart
              title="Recorrido por vehículos — KM"
              units={data.travel_by_vehicle_month}
              months={data.months}
              metricKey="kilometers"
              unit="km"
              layout="vertical"
              meta={meta}
              exportConfig={{
                kind: 'travel',
                metricLabel: 'Kilómetros',
                details: data.travel_detail_vehicle || [],
              }}
            />
            <GroupedUnitMonthBarChart
              title="Rendimiento por vehículo — KM/GAL"
              units={data.efficiency_vehicle_month}
              months={data.months}
              metricKey="km_per_gallon"
              unit="km/gal"
              layout="vertical"
              meta={meta}
              exportConfig={{
                kind: 'efficiency',
                metricLabel: 'Km por galón',
                efficiencyMode: 'km_per_gallon',
                fuelDetails: data.fuel_detail_vehicle || [],
                travelDetails: data.travel_detail_vehicle || [],
              }}
            />
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <GroupedUnitMonthBarChart
              title="Combustible por maquinaria — Galones"
              units={data.fuel_by_machine_month}
              months={data.months}
              metricKey="gallons"
              unit="gal"
              layout="horizontal"
              meta={meta}
              exportConfig={{
                kind: 'fuel',
                metricLabel: 'Galones',
                details: data.fuel_detail_machine || [],
              }}
            />
            <GroupedUnitMonthBarChart
              title="Combustible por maquinaria — Soles"
              units={data.fuel_by_machine_month}
              months={data.months}
              metricKey="soles"
              currency
              layout="horizontal"
              meta={meta}
              exportConfig={{
                kind: 'fuel',
                metricLabel: 'Soles (S/)',
                details: data.fuel_detail_machine || [],
              }}
            />
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <GroupedUnitMonthBarChart
              title="Recorrido por maquinaria — HM"
              units={data.travel_by_machine_month}
              months={data.months}
              metricKey="horometro"
              unit="HM"
              layout="vertical"
              meta={meta}
              exportConfig={{
                kind: 'travel',
                metricLabel: 'Δ Horómetro (HM)',
                details: data.travel_detail_machine || [],
              }}
            />
            <GroupedUnitMonthBarChart
              title="Consumo por equipo — GAL/HM"
              units={data.efficiency_machine_month}
              months={data.months}
              metricKey="gal_per_hm"
              unit="gal/HM"
              layout="vertical"
              meta={meta}
              exportConfig={{
                kind: 'efficiency',
                metricLabel: 'Galones por HM',
                efficiencyMode: 'gal_per_hm',
                fuelDetails: data.fuel_detail_machine || [],
                travelDetails: data.travel_detail_machine || [],
              }}
            />
          </div>
        </section>
      )}

      {/* Alertas */}
      {alertsUrgent.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 border-l-4 border-l-amber-500">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-semibold text-amber-900">Alertas de mantenimiento</h3>
            <Link to="/machinery/maintenance" className="text-sm font-medium text-amber-700 hover:text-amber-900">
              Ver todas →
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {alertsUrgent.slice(0, 6).map((item, idx) => (
              <div key={`${item.type}-${item.id}-${idx}`} className="flex items-center justify-between p-3 bg-white rounded-lg border border-amber-100 text-sm">
                <div>
                  <span className="font-medium text-gray-900">{item.code} — {item.name}</span>
                  <span className={`ml-2 text-xs px-2 py-0.5 rounded font-medium ${item.status === 'alerta' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'}`}>
                    {item.status === 'alerta' ? 'Vencido' : 'Pronto'}
                  </span>
                </div>
                <span className="text-gray-600 text-xs whitespace-nowrap ml-2">
                  {item.unit === 'km' && item.remaining_km != null ? `${formatNumber(item.remaining_km, 0)} km` : ''}
                  {item.unit === 'horas' && item.remaining_hours != null ? `${formatNumber(item.remaining_hours, 1)} h` : ''}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ─── FINANCIERO ─── */}
      <section className="space-y-6">
        <SectionTitle
          title="Indicadores financieros"
          subtitle="Gastos de la flota sin IGV en el mismo período"
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard
            label="Gasto total flota"
            value={formatCurrency(kpis.total_spend)}
            sub={`Veh. ${formatCurrency(kpis.vehicle_spend)} · Máq. ${formatCurrency(kpis.machine_spend)}`}
            borderColor="border-teal-500"
            icon="💰"
          />
          <KpiCard
            label="Costo por hora laborada"
            value={kpis.cost_per_hour != null ? formatCurrency(kpis.cost_per_hour) : '—'}
            sub="Gasto total ÷ horas de jornada"
            borderColor="border-emerald-500"
            icon="📈"
          />
          <KpiCard
            label="Costo por km"
            value={kpis.cost_per_km != null ? formatCurrency(kpis.cost_per_km) : '—'}
            sub="Gasto total ÷ km de jornada"
            borderColor="border-lime-500"
            icon="📈"
          />
          <KpiCard
            label="Inconsistencias monetarias"
            value={formatNumber(kpis.monetary_inconsistencies, 0)}
            sub={`${formatNumber(kpis.total_events, 0)} eventos en bitácora`}
            borderColor="border-red-500"
            icon="⚠️"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {assetTypeData.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
              <h3 className="text-base font-semibold text-gray-800 mb-4">Gasto: vehículos vs maquinarias</h3>
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={assetTypeData}
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={95}
                    paddingAngle={2}
                    dataKey="value"
                    nameKey="name"
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  >
                    {assetTypeData.map((_, i) => (
                      <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={currencyTooltip} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
          {lineData.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
              <h3 className="text-base font-semibold text-gray-800 mb-4">Gasto por línea de activo</h3>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={lineData} layout="vertical" margin={{ left: 8, right: 16 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis type="number" tickFormatter={(v) => `S/ ${(v / 1000).toFixed(0)}k`} />
                  <YAxis type="category" dataKey="label" width={120} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={currencyTooltip} />
                  <Bar dataKey="total_spend" fill="#0F766E" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {monthData.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
              <h3 className="text-base font-semibold text-gray-800 mb-4">Evolución del gasto mensual</h3>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={monthData} margin={{ top: 8, right: 16, left: 8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={(v) => `S/ ${(v / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={currencyTooltip} />
                  <Line type="monotone" dataKey="total_spend" stroke="#0F766E" strokeWidth={2} dot={{ r: 4 }} name="Gasto" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
          {eventTypeData.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
              <h3 className="text-base font-semibold text-gray-800 mb-4">Gasto por tipo de evento</h3>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={eventTypeData} margin={{ bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="event_type_name" angle={-35} textAnchor="end" interval={0} tick={{ fontSize: 10 }} height={70} />
                  <YAxis tickFormatter={(v) => `S/ ${(v / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={currencyTooltip} />
                  <Bar dataKey="total_spend" radius={[4, 4, 0, 0]}>
                    {eventTypeData.map((_, i) => (
                      <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {topAssetsData.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
            <h3 className="text-base font-semibold text-gray-800 mb-4">Top unidades por gasto</h3>
            <ResponsiveContainer width="100%" height={Math.max(240, topAssetsData.length * 36)}>
              <BarChart data={topAssetsData} layout="vertical" margin={{ left: 8, right: 16 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis type="number" tickFormatter={(v) => `S/ ${(v / 1000).toFixed(0)}k`} />
                <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 11 }} />
                <Tooltip formatter={currencyTooltip} />
                <Bar dataKey="value" fill="#3B82F6" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {(topVehicles.length > 0 || topMachines.length > 0) && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {topVehicles.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                <h3 className="text-base font-semibold text-gray-800 px-5 py-4 border-b border-gray-100">Top vehículos (gasto y uso)</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Código</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-600 uppercase">Gasto</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-600 uppercase">Horas</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-600 uppercase">Km</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-600 uppercase">Δ Horóm.</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {topVehicles.map((r, i) => (
                        <tr key={i} className="hover:bg-gray-50/50">
                          <td className="px-4 py-2 text-gray-900">{r.code}</td>
                          <td className="px-4 py-2 text-right font-medium">{formatCurrency(r.total_spend)}</td>
                          <td className="px-4 py-2 text-right">{formatNumber(r.total_hours)}</td>
                          <td className="px-4 py-2 text-right">{formatNumber(r.total_kilometers, 0)}</td>
                          <td className="px-4 py-2 text-right">{formatNumber(r.total_horometro)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {topMachines.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                <h3 className="text-base font-semibold text-gray-800 px-5 py-4 border-b border-gray-100">Top maquinarias (gasto y uso)</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Código</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-600 uppercase">Gasto</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-600 uppercase">Horas</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-600 uppercase">Km</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-600 uppercase">Δ Horóm.</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {topMachines.map((r, i) => (
                        <tr key={i} className="hover:bg-gray-50/50">
                          <td className="px-4 py-2 text-gray-900">{r.code}</td>
                          <td className="px-4 py-2 text-right font-medium">{formatCurrency(r.total_spend)}</td>
                          <td className="px-4 py-2 text-right">{formatNumber(r.total_hours)}</td>
                          <td className="px-4 py-2 text-right">{formatNumber(r.total_kilometers, 0)}</td>
                          <td className="px-4 py-2 text-right">{formatNumber(r.total_horometro)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </section>

      {!hasUsage && !hasFinance && (
        <div className="text-center py-12 text-gray-500 bg-white border border-gray-200 rounded-xl">
          No hay datos de uso ni gastos en el período seleccionado. Verifique que existan jornadas (Inicio/Fin) registradas.
        </div>
      )}
    </div>
  );
}
